import { Checkbox, CheckboxRootProps, mergeRefs } from '@chakra-ui/react';
import {
  ChangeEventHandler,
  FocusEventHandler,
  ReactNode,
  forwardRef,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

export interface MCheckboxProps extends Omit<
  CheckboxRootProps,
  'checked' | 'disabled' | 'readOnly' | 'onChange' | 'onBlur' | 'onFocus'
> {
  isChecked?: boolean;
  isIndeterminate?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  id?: string;
  name?: string;
  required?: boolean;
  autoFocus?: boolean;
  tabIndex?: number;
  'data-testid'?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  children?: ReactNode;
}

/*
 * v3 Checkbox is namespace-only (`Checkbox.Root/Control/HiddenInput/Label`),
 * no bare renderable component. This wrapper keeps the v2 call-site shape
 * (`isChecked`/`isDisabled`/`onChange`, children as the label) so consumers
 * don't have to know about the compound structure underneath — see
 * `styles/chakra/components/Checkbox.ts` for the recipe this composes
 * against.
 *
 * Props split two ways: `id`/`name`/`aria-*`/the event handlers/the
 * forwarded ref all land on `Checkbox.HiddenInput` — the real native
 * `<input>` and the only element with an accessible role (QA's
 * `getByRole('checkbox', { name })` and RHF's `register`/`ref` both need the
 * real input, not the `Root` label wrapper). Everything else (style props,
 * `checked`/`disabled`/`readOnly` state, `variant`/`size`) goes on `Root`.
 *
 * `data-testid` goes on `Root` too, as it did in v2 (the root `<label>`) and
 * as in `MSwitch`. QA's e2e suite clicks these testids and finds the checkbox
 * inside them (MNNOW-3024); Playwright never finds a stable box on the 1×1
 * clipped input, and its checked/disabled assertions follow a label to its
 * control, so they still read the input.
 *
 * `id` is the exception: it goes to `Root` as `ids.hiddenInput` so zag uses
 * it for both the input's `id` and the `Root` label's `htmlFor` (same fix as
 * `MSwitch`). Without an `id`, a checkbox inside a `Field` takes the field's
 * control id, which is only right for a lone checkbox in that field.
 */
export const MCheckbox = forwardRef<HTMLInputElement, MCheckboxProps>(
  (
    {
      isChecked,
      isIndeterminate,
      isDisabled,
      isReadOnly,
      onChange,
      onBlur,
      onFocus,
      id,
      name,
      required,
      autoFocus,
      tabIndex,
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      children,
      ...rootProps
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    // Memoized so the hidden input's refs aren't detached and re-attached on
    // every render
    const hiddenInputRef = useMemo(() => mergeRefs(ref, inputRef), [ref]);

    // zag syncs a controlled change onto the hidden input through the
    // prototype's `checked` setter, which bypasses React's value tracker. The
    // tracker keeps the old value, so the next click back to it reads as no
    // change and React drops the onChange: after "Select All" unticked a list,
    // ticking a row did nothing. The element's own setter updates the tracker.
    useLayoutEffect(() => {
      const isControlled = isChecked !== undefined || !!isIndeterminate;
      if (inputRef.current && isControlled) {
        inputRef.current.checked = !isIndeterminate && !!isChecked;
      }
    }, [isChecked, isIndeterminate]);

    return (
      <Checkbox.Root
        checked={isIndeterminate ? 'indeterminate' : isChecked}
        disabled={isDisabled}
        readOnly={isReadOnly}
        {...(id && { ids: { hiddenInput: id } })}
        data-testid={dataTestId}
        {...rootProps}
      >
        <Checkbox.HiddenInput
          ref={hiddenInputRef}
          name={name}
          required={required}
          autoFocus={autoFocus}
          tabIndex={tabIndex}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          // zag's read-only guard only cancels the click, and React fires a
          // checkbox's onChange from that same click — so the toggle reached
          // forms on locked screens. v2 skipped onChange when read-only.
          onChange={isReadOnly ? undefined : onChange}
          onBlur={onBlur}
          onFocus={onFocus}
        />
        <Checkbox.Control />
        {children && <Checkbox.Label>{children}</Checkbox.Label>}
      </Checkbox.Root>
    );
  },
);

MCheckbox.displayName = 'MCheckbox';

export default MCheckbox;
